import { Box } from '@packages/shared';
import type { LinkBoxType } from './types';
import { registerComponents } from '../ComponentRecognizer/componentRegistry';
import { ComponentTypes } from '../../../interfaces';
import { ImageTeaser } from '../ImageTeaser';
import { LinkBoxItemContent } from './LinkBox.item.content';
import { buildLinkWithArguments } from '../../utils';

/**
 * The LinkBox component displays a teaser with maintainable texts and different layout variants
 */
export const LinkBox = ({ data }: { data: LinkBoxType }) => {
  const {
    // layout of the box
    // Options:
    // 	• Bottom (Standard, Text is below the image)
    // 	• Top (Text is above the image)
    // 	• Left (Text is to the left of the image)
    // 	• Right (Text is to the right of the image)
    behavior_paragraph_alignment: layoutAlignment = 'bottom',
    // Options:
    // 	• standard (without background color)
    // 	• boxed (with bg color)
    behavior_paragraph_variant: layoutVariant = 'standard',
    // Custom Margin
    behavior_paragraph_margin: layoutMargin = '0',

    // link
    field_link: link,
    field_link_anchor: linkAnchor,
    field_link_highlight: linkHighlight,
    field_link_searchfilter: linkSearchFilter,
    field_tracking: linkTracking,
    field_bonus_number: bonusNumber,

    // custom identifier e.G. dynamic yield
    behavior_paragraph_custom_id: customId,
  } = data;

  const finalLink: string = link
    ? buildLinkWithArguments(link, {
        field_link_anchor: linkAnchor,
        field_link_highlight: linkHighlight,
        field_link_searchfilter: linkSearchFilter,
        field_tracking: linkTracking,
        field_bonus_number: bonusNumber,
      })
    : '';

  const isLandscape: boolean = layoutAlignment === 'left' || layoutAlignment === 'right';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isLandscape ? 'row' : 'column',
        alignItems: 'flex-start',
        height: '100%',
        backgroundColor: layoutVariant === 'boxed' ? 'info.light' : 'transparent',
        margin: layoutMargin,
      }}
      data-dy={customId}
    >
      <Box
        sx={{
          display: 'flex',
          ...((layoutAlignment === 'top' || layoutAlignment === 'left') && { order: 1 }),
          width: isLandscape ? '50%' : '100%',
          height: isLandscape ? '100%' : 'auto',
          '& img, & a': {
            display: 'block',
            width: '100%',
          },
          '& img': {
            objectFit: 'cover',
            height: isLandscape ? '100%' : 'auto !important',
          },
        }}
      >
        <ImageTeaser data={data} link={finalLink} />
      </Box>
      <LinkBoxItemContent
        data={{
          ...data,
          behavior_paragraph_variant: layoutVariant,
        }}
        link={finalLink}
        isLandscape={isLandscape}
      />
    </Box>
  );
};

// Needed to run this component in Storybook and Jest tests. Register the component in a global componentRegistry object
registerComponents({
  [ComponentTypes.LinkBox]: LinkBox,
});
